// OnshapeSyncReview — ONE BUTTON, ONE LIST, ONE OK (ON-21).
//
// Alex, 2026-08-19: "in as little API call as possible get as much as possible...
// directly build the config... directly build the component list... only the
// opportunity to check / uncheck. Or flag as error or advice."
//
// It replaces two panels and their two review flows on the product screen:
//   • CompsSyncPanel   — Preview → read the diff → Apply
//   • ConfigCheckPanel — Check   → read the findings → OK each one
// Same two reads, same two stores, same writes. What changed is the SHAPE of the
// work: one click reads both halves concurrently, everything lands as rows in one
// list, and one OK writes exactly what is ticked.
//
// ⚠ THE HONEST COST. Onshape has no batch endpoint, so this spends the same 2 + 1
// calls the two buttons did. The saving is that they overlap, and that the review
// is one list instead of two — not a cheaper read. The button says the number.
//
// WHAT ARRIVES TICKED, and it is one rule (the server decides it — `pre_tick` on
// every row; see findingStance in product-db.js): a row is ticked when it only
// ADDS what the DB lacks, and unticked when it OVERWRITES something already
// there. Accepting a missing param or an empty range costs nothing if it was
// wrong. Replacing a default Alex set deliberately is the sync overwriting
// engineering judgement — which is the failure this list exists to prevent.
//
// Rows that need the MODEL or a decision (not in Onshape, kind mismatch, enum
// option drift) carry no tick box at all: advice, not work.
//
// COMPONENTS NEVER WAIT HERE (2026-10-12). Every add, qty update and remove the
// read plans is written by the read itself, and a write left unfinished is
// finished by the next Refresh (writeSyncPlan) — so a part lands in Components,
// Not synced / TBD or Not applicable, never in a tick list. This list is now the
// CONFIGURATION findings only.
//
// Everything renders from the STORES (ON-19/ON-20), so the list survives leaving
// the page — the thing Alex lost before: "as soon as I move from one page and I
// come back, I lose everything."
//
// Colour-blind safe (RULES 3): every group and every tick says its WORD.

import { useState, useEffect, useCallback } from 'react';
import { BTN_PRIMARY, BTN_PRIMARY_OUTLINE, BTN_DISABLED } from '../theme-tokens.js';
import { SYNC, OK, WARN, DASH } from '../icons.js';
import CheckboxCell from './CheckboxCell.jsx';
import Text from './Text.jsx';

// KINDS + findingText lived in ConfigCheckPanel until that panel retired
// (ON-21 step 4, 2026-08-21) — one sentence per finding kind, written once.
const KINDS = {
  missing_in_db:      { word: 'not in the DB' },
  missing_in_onshape: { word: 'not in Onshape' },
  value_drift:        { word: 'default differs' },
  range_drift:        { word: 'range differs' },
  enum_axis_drift:    { word: 'options differ' },
  kind_mismatch:      { word: 'shape differs' },
  would_fill:         { word: 'range is empty' },
  size_bindable:      { word: 'now follows' },
};

function findingText(f) {
  const d = f.detail || {};
  switch (f.kind) {
    case 'missing_in_db':
      if (d.kind === 'enum') {
        // ON-21 (c): when the check has worked out WHICH size list the axis speaks,
        // the row must say what the tick will build — a param, its list, its span —
        // because that is what is being agreed to, not merely "an enum of 3 options".
        return d.proposal
          ? <><strong>{d.onshape_name}</strong>
              {d.proposal.list_name === 'custom'
                ? ` — a custom list (${d.proposal.looks.join(', ')})`
                : ` — a ${d.proposal.list_name} size list (${d.proposal.looks.join(', ')})`}
              {d.proposal.typical_text ? `, typical ${d.proposal.typical_text}` : ''}
              {' — the tick creates this param'}</>
          : <><strong>{d.onshape_name}</strong>{` — an enum of ${d.option_count} options, default ${String(d.default ?? DASH)}; its options overlap a size list but match none exactly — pick or fix the list, then re-check`}</>;
      }
      return <><strong>{d.onshape_name}</strong>{` — Onshape default ${String(d.default ?? DASH)}${d.units ? ` ${d.units}` : ''}${d.min != null && d.max != null ? `, range ${d.min}…${d.max}` : ''}`}</>;
    case 'missing_in_onshape':
      return <><strong>{d.param_name}</strong>{' — the product has it; the Onshape configuration does not drive it'}</>;
    case 'value_drift':
      return <><strong>{d.param_name}</strong>{` — Onshape ${String(d.onshape_value)} vs DB ${String(d.db_value)}`}</>;
    case 'range_drift':
      return <><strong>{d.param_name}</strong>{` — Onshape ${d.onshape_min}…${d.onshape_max} vs DB ${d.db_min}…${d.db_max}`}</>;
    case 'enum_axis_drift': {
      const parts = [];
      if (d.options_not_in_db?.length) parts.push(`Onshape offers ${d.options_not_in_db.map(o => o.option).join(', ')} with no size row`);
      if (d.sizes_not_in_onshape?.length) parts.push(`${d.sizes_not_in_onshape.map(x => x.look || x.size_id).join(', ')} in range but not offered by the model`);
      return <><strong>{d.param_name}</strong>{` — vs size list ${d.list_name}: ${parts.join('; ')}`}</>;
    }
    case 'kind_mismatch':
      return <><strong>{d.param_name}</strong>{` — Onshape drives it as an enum; the DB param is ${d.db_type || 'not a list'}`}</>;
    case 'would_fill':
      return <><strong>{d.param_name}</strong>{` — both ends empty; Onshape could fill ${d.min}…${d.max}`}</>;
    // ON-18. Normally applied automatically (it changes nothing at today's size —
    // see findingStance), so this text is what a reader sees in the applied list or
    // in the rare case the automatic write did not take it. It still states the
    // EVIDENCE, because "follows the parameter" was inferred, not read.
    case 'size_bindable':
      return <><strong>{d.comp_label}</strong>{` follows ${d.param_name} instead of staying pinned at ${d.look || d.size_id} — its model config says ${d.axis}=${d.size_id}, which is where ${d.param_name} sits.`}</>;
    default:
      return <strong>{f.finding_key}</strong>;
  }
}

// 2 for the BOM (a part studio needs 1; the /p 204 that says "assembly" plus the
// BOM makes 2) + 1 for the configuration. Stated before the click — RULES 10.
const READ_CALLS = 3;

const GroupTitle = ({ children, note }) => (
  <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, marginTop: 8, marginBottom: 2 }}>
    {children}
    {note && <span style={{ fontWeight: 400, color: 'var(--th-text-hint)' }}> — {note}</span>}
  </div>
);

// One tickable row: the tick, the action WORD, the sentence. The word column is
// the same width in both sections so components and configuration read as one
// list rather than two tables that happen to be adjacent.
function TickRow({ ticked, onTick, word, children, note }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
      <span style={{ flexShrink: 0, width: 92 }}>
        <CheckboxCell value={ticked} onChange={onTick} label={['apply', 'skip']} title="Tick to include in the OK below" />
      </span>
      <span style={{
        color: 'var(--th-text-hint)', fontSize: 'var(--fs-micro)', width: 108, flexShrink: 0,
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>{word}</span>
      <span style={{ fontSize: 'var(--fs-small)', flex: 1 }}>
        {children}
        {note && <span style={{ color: 'var(--th-text-hint)' }}> {note}</span>}
      </span>
    </div>
  );
}

// Minutes is the useful grain for "when did I last look" — same rule as the
// worklist's stamp.
function stamp(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function OnshapeSyncReview({ productId, onshapeRef, syncedAt, onApplied, onRead, refreshToken }) {
  const [status, setStatus] = useState('idle'); // idle | reading | error
  const [error, setError] = useState(null);
  const [findings, setFindings] = useState([]);
  const [readErrors, setReadErrors] = useState(null);
  const [ticks, setTicks] = useState({});          // key -> bool
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [rowErr, setRowErr] = useState({});
  // WHERE EVERY PART WENT (2026-10-11) — the one line that proves the three
  // sections below cover the read: parts = components + not synced + not
  // applicable. Replanned from the cached read by the server, 0 Onshape calls.
  const [tally, setTally] = useState(null);

  // The stores are the source of truth, so a fresh mount shows the last read —
  // no Onshape call, and the reason the list survives navigation.
  const load = useCallback(() => {
    Promise.all([
      fetch(`/api/products/${productId}/config-findings`)
        .then(r => (r.ok ? r.json() : null))
        .then(rows => { if (Array.isArray(rows)) setFindings(rows); })
        .catch(() => {}),
      fetch(`/api/products/${productId}/sync-accounting`)
        .then(r => (r.ok ? r.json() : null))
        .then(b => setTally(b?.tally || null))
        .catch(() => {}),
    ]);
  }, [productId]);
  useEffect(() => {
    setResult(null);
    setFindings([]); setTally(null);
    load();
    // refreshToken: the host bumps it after a param edit reruns the CACHED
    // comparison server-side, so a finding closes the moment the DB catches up
    // instead of waiting for the next paid read.
  }, [load, refreshToken]);

  const openFindings = findings.filter(f => f.status === 'open' && f.disposition === 'open');
  const tickable = openFindings.filter(f => f.appliable);
  const advice = openFindings.filter(f => !f.appliable);
  const intentional = findings.filter(f => f.disposition === 'intentional' && f.status === 'open');

  // Defaults come from the SERVER's pre_tick, and are recomputed whenever the set
  // of rows changes identity (a new read replaces the list). Ticks the user has
  // changed inside one list survive re-renders because the key set is unchanged.
  const rowKey = tickable.map(f => `f${f.id}`).join(',');
  useEffect(() => {
    const next = {};
    for (const f of tickable) next[`f${f.id}`] = Boolean(f.pre_tick);
    setTicks(next);
    // rowKey is the identity of the row set; the arrays themselves are new objects
    // on every render and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  const read = async () => {
    setStatus('reading'); setError(null); setResult(null); setReadErrors(null);
    try {
      const r = await fetch(`/api/products/${productId}/onshape-read?budget=${READ_CALLS}`);
      const text = await r.text();
      let b;
      try { b = JSON.parse(text); }
      catch {
        // A non-JSON reply is almost always a server running code from before this
        // route existed — name that fact instead of a parse error (2026-08-19).
        throw new Error(r.status === 404
          ? 'The server does not know this route yet — restart the app and retry.'
          : `The server replied ${r.status} with something that is not JSON — check the server log.`);
      }
      if (!r.ok) throw new Error(b.error || `Read failed (${r.status})`);
      if (Array.isArray(b.findings)) setFindings(b.findings);
      setReadErrors(b.errors && Object.keys(b.errors).length ? b.errors : null);
      setStatus('idle');
      onRead?.();

      // ⚠ THE CLEAN HALF GOES STRAIGHT IN — no confirmation (Alex, 2026-08-20:
      // "if everything was OK, the plan was to directly transfer to the config
      // without having the intermediate confirmation. And I had to confirm.").
      // He asked for this twice: "directly build the config from Onshape (no
      // validation nothing). Only the opportunity to check / uncheck." A row that
      // needs no decision should not cost a click to accept — the review list is
      // there for the rows that DO.
      //
      // What may ride in automatically is exactly `pre_tick` — and since
      // 2026-08-20 ("simply update all and nothing more") that is every row the
      // read produced, removes included: the sync's one job is to make the list
      // mirror the model. What made removes safe is upstream in planSync — a
      // part that merely failed to resolve never reaches the remove list, so
      // its comp survives the hiccup. Drifts still wait for a tick (they would
      // overwrite a value Alex chose), manual rows are never touched, and the
      // write goes through the POST route, so the DB is snapshotted first and
      // the whole thing is undoable from History.
      const rows = b.pending?.rows || [];
      const autoComps = rows.filter(x => x.pre_tick && x.action !== 'remove').map(x => x.id);
      const autoRemoves = rows.filter(x => x.pre_tick && x.action === 'remove').map(x => x.id);
      const autoFinds = (b.findings || [])
        .filter(f => f.status === 'open' && f.disposition === 'open' && f.pre_tick)
        .map(f => f.id);
      if (autoComps.length || autoFinds.length || autoRemoves.length) {
        await apply(autoComps, autoFinds, false, null, autoRemoves);
      }

      // Always re-read the stores after a read: the not-synced COUNT comes from
      // sync-issues, which this route has just rewritten, and a half that FAILED
      // left its store untouched and therefore stale. Either way the list must
      // never show one fresh half beside an old one.
      load();
    } catch (e) { setError(e.message); setStatus('error'); }
  };

  // `second` marks the follow-up pass described below; it exists only to bound the
  // recursion to one extra round, so a server that somehow keeps producing ticked
  // rows can never spin here.
  const apply = async (autoComps, autoFindings, second = false, carry = null, autoRemoves = null) => {
    const auto = Array.isArray(autoComps);
    // Components are never ticked by hand any more (2026-10-12): the read writes
    // all of them, so only the automatic pass carries comp ids.
    const comps = auto ? autoComps : [];
    const fids = auto ? autoFindings : tickable.filter(f => ticks[`f${f.id}`]).map(f => f.id);
    const removes = auto ? (autoRemoves || []) : [];
    if (!comps.length && !fids.length && !removes.length) return;
    setApplying(true); setError(null);
    try {
      const r = await fetch(`/api/products/${productId}/onshape-apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comps, findings: fids, removes }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `Apply failed (${r.status})`);
      if (b.findings?.findings) setFindings(b.findings.findings);
      // ⚠ ONE FOLLOW-UP PASS, and it is not belt-and-braces — without it the feature
      // is a click short. A size binding can only be proposed once the component
      // EXISTS, and components are written by this very call; the server therefore
      // re-derives candidates after its writes and returns them. Those are ticked
      // rows that arrived too late for the pass that is finishing, so a component
      // added and bound in the same sync would otherwise sit waiting for an OK that
      // the whole point was to remove.
      const mine = {
        added: b.comps?.applied?.added ?? 0,
        updated: b.comps?.applied?.updated ?? 0,
        removed: b.removed?.removed?.removed ?? 0,
        params: b.findings?.applied ?? 0,
        errors: b.errors || [],
        auto,
      };
      if (auto && !second) {
        const late = (b.findings?.findings || [])
          .filter(f => f.status === 'open' && f.disposition === 'open' && f.pre_tick)
          .map(f => f.id);
        // The counts ACCUMULATE across the two passes — reporting only the second
        // would say "0 added" right after adding five components.
        if (late.length) { await apply([], late, true, mine); return; }
      }
      setResult(carry
        ? { ...mine, added: mine.added + carry.added, updated: mine.updated + carry.updated,
            removed: mine.removed + carry.removed,
            params: mine.params + carry.params, errors: [...carry.errors, ...mine.errors], auto: true }
        : mine);
      onApplied?.();
    } catch (e) { setError(e.message); }
    setApplying(false);
  };

  const dispose = (fid, disposition) => {
    setRowErr(e => ({ ...e, [fid]: null }));
    fetch(`/api/products/${productId}/config-findings/${fid}/disposition`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ disposition }),
    })
      .then(async r => {
        const b = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(b.error || `Failed (${r.status})`);
        return b;
      })
      .then(rows => { if (Array.isArray(rows)) setFindings(rows); })
      .catch(e => setRowErr(prev => ({ ...prev, [fid]: e.message })));
  };

  if (!onshapeRef) return null;

  const tickedCount = Object.values(ticks).filter(Boolean).length;

  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--th-border-subtle)' }}>
      <button onClick={read} disabled={status === 'reading'} style={{ ...BTN_PRIMARY_OUTLINE, ...(status === 'reading' ? BTN_DISABLED : null) }}>
        {status === 'reading' ? 'Reading Onshape…'
          : applying ? 'Writing…'
          : `${SYNC} Sync from Onshape — up to ${READ_CALLS} calls`}
      </button>
      {/* Beside the button: WHEN, not HOW (Alex, 2026-08-21: the how-it-works
          caption "is useless... this is the progress we did"). One stamp, from
          products.onshape_synced_at — durable, unlike the pending store, which
          drains the moment a sync auto-applies. */}
      <span style={{ marginLeft: 8, fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>
        {syncedAt ? `last sync ${stamp(syncedAt)}` : 'never synced'}
      </span>
      {/* Words, not colour: "adds up" or "does NOT add up". */}
      {tally && status !== 'reading' && (
        <div style={{ marginTop: 6, fontSize: 'var(--fs-small)', color: tally.balanced ? 'var(--th-text-secondary)' : 'var(--th-status-deprecated)' }}>
          {tally.balanced ? null : `${WARN} `}
          {tally.parts} part{tally.parts === 1 ? '' : 's'} read = {tally.components} in Components
          {' + '}{tally.notSynced} Not synced / TBD + {tally.notApplicable} Not applicable
          {tally.balanced ? '' : ' — does NOT add up, report this'}
        </div>
      )}
      {status === 'error' && (
        <Text kind="deprecated" as="div" style={{ marginTop: 6 }}>{WARN} {error}</Text>
      )}
      {/* One half down must never look like "that half is clean". */}
      {readErrors && (
        <Text kind="deprecated" as="div" style={{ marginTop: 6 }}>
          {readErrors.components && <div>{WARN} Components not read — {readErrors.components}</div>}
          {readErrors.configuration && <div>{WARN} Configuration not read — {readErrors.configuration}</div>}
          <Text kind="hint" as="div">
            Rows below are from the last successful read of that half, not from this one.
          </Text>
        </Text>
      )}

      {result && (
        <div style={{ marginTop: 6, fontSize: 'var(--fs-small)', color: 'var(--th-status-synced)' }}>
          {OK} {result.auto ? 'Synced' : 'Applied'} — {result.added} added, {result.updated} updated, {result.removed} removed, {result.params} param{result.params === 1 ? '' : 's'}.
          {/* An automatic write must say where its undo lives, or "no confirmation"
              becomes "no way back". The POST snapshots the DB before writing. */}
          {result.auto && (result.added || result.updated || result.params) ? (
            <span style={{ color: 'var(--th-text-hint)' }}> Undo from History if that was wrong.</span>
          ) : null}
          {result.errors.length > 0 && (
            <div style={{ color: 'var(--th-status-deprecated)' }}>
              {WARN} {result.errors.length} row{result.errors.length === 1 ? '' : 's'} refused: {result.errors.map(e => e.error).join('; ')}
            </div>
          )}
        </div>
      )}


      {tickable.length > 0 && (
        <div style={{ marginTop: 8, padding: 8, background: 'var(--tg-node-l0-bg)', borderRadius: 4 }}>
          {/* ⚠ TWO GROUPS, SPLIT ON THE ROW'S OWN pre_tick, because one heading over
              both told a lie in the state that matters. Rows that ride in
              automatically normally never appear here — the read applies them — but
              they DO appear when a previous read's write did not finish, and then a
              heading reading "needs your decision" sat above rows that were already
              ticked and needed nothing of the sort. Found by loading the page.
              Nothing is ever applied by merely LOOKING at it, so these wait for the
              OK rather than writing themselves on mount. */}
          {tickable.filter(f => f.pre_tick).length > 0 && (
            <>
              <GroupTitle note="from a read whose write did not finish — the OK below applies them">
                Ready to apply — {tickable.filter(f => f.pre_tick).length}
              </GroupTitle>
              {tickable.filter(f => f.pre_tick).map(f => (
                <div key={`f${f.id}`}>
                  <TickRow ticked={ticks[`f${f.id}`]}
                    onTick={v => setTicks(t => ({ ...t, [`f${f.id}`]: v }))}
                    word={KINDS[f.kind]?.word || f.kind}>
                    {findingText(f)}
                  </TickRow>
                  {rowErr[f.id] && (
                    <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-status-deprecated)', marginLeft: 200 }}>
                      {WARN} {rowErr[f.id]}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {tickable.filter(f => !f.pre_tick).length > 0 && (
            <>
              {/* What is LEFT after the automatic write: rows that would REPLACE a
                  value the DB already holds. That is the whole reason a review list
                  still exists. */}
              <GroupTitle note="each replaces a value you already have — tick the ones to accept">
                Needs your decision — {tickable.filter(f => !f.pre_tick).length}
              </GroupTitle>
              {tickable.filter(f => !f.pre_tick).map(f => (
                <div key={`f${f.id}`}>
                  <TickRow ticked={ticks[`f${f.id}`]}
                    onTick={v => setTicks(t => ({ ...t, [`f${f.id}`]: v }))}
                    word={KINDS[f.kind]?.word || f.kind}>
                    {findingText(f)}
                  </TickRow>
                  {rowErr[f.id] && (
                    <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-status-deprecated)', marginLeft: 200 }}>
                      {WARN} {rowErr[f.id]}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* THE one OK. It writes what is ticked and says so before it is clicked. */}
          <button onClick={apply} disabled={applying || tickedCount === 0} style={{
            ...BTN_PRIMARY, display: 'block', marginTop: 10, width: '100%', textAlign: 'center',
            ...((applying || tickedCount === 0) ? BTN_DISABLED : null),
          }}>
            {applying ? 'Applying…'
              : tickedCount === 0 ? 'Nothing ticked'
              : `OK — apply ${tickedCount} ticked row${tickedCount === 1 ? '' : 's'} (no Onshape calls)`}
          </button>
        </div>
      )}

      {/* ⚠ THE PARTS THE SYNC REFUSED ARE NOT REPEATED HERE — only counted, with
          the name of the section that holds them. Alex, 2026-08-20, on seeing the
          same five rows twice down one screen: "we don't need all this information,
          at least the ones that are in the sync list."
          They were printed here because "the one list is the whole story" — but the
          Not synced section a few inches below is DURABLE (it survives previews and
          tracks open/resolved per row), and this copy was a snapshot of one run.
          Two copies of one fact means a reader must work out whether they agree.
          The count belongs here, because it is part of "what did this read find";
          the rows belong there, because that is where they are worked. */}
      {/* ADVICE — no tick box, because no DB-side write is the fix. Either the
          model changes, or the difference is deliberate and says so once. These
          are CONFIG findings and appear nowhere else, so they are shown in full. */}
      {advice.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--th-border-subtle)' }}>
          <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--th-status-deprecated)' }}>
            {WARN} Configuration — needs a decision ({advice.length})
          </div>
          <Text kind="hint" as="div" style={{ marginBottom: 4 }}>
            No tick box: the fix is in the model, not in a value the OK could write.
          </Text>
          {advice.map(f => (
            <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
              <span style={{
                color: 'var(--th-text-hint)', fontSize: 'var(--fs-micro)', width: 108, flexShrink: 0,
                textTransform: 'uppercase', letterSpacing: 0.4,
              }}>{KINDS[f.kind]?.word || f.kind}</span>
              <span style={{ fontSize: 'var(--fs-small)', flex: 1 }}>{findingText(f)}</span>
              <button style={{ ...BTN_PRIMARY_OUTLINE, fontSize: 'var(--fs-micro)', padding: '1px 8px' }}
                onClick={() => dispose(f.id, 'intentional')}
                title="This difference is deliberate — stop reporting it, permanently, even on re-reads.">
                Intentional
              </button>
            </div>
          ))}
        </div>
      )}

      {/* The "Gone from the model" block that stood here is GONE ITSELF (Alex,
          2026-08-20, after seeing the same five removes in four places down one
          screen: "everything is kind of informed 4 times... simply update all and
          nothing more"). Removes now apply with the read like every other row,
          and a write that did not finish is finished by the next Refresh. */}

      {intentional.length > 0 && (
        <Text kind="hint" as="div" style={{ marginTop: 8 }}>
          {intentional.length} difference{intentional.length === 1 ? '' : 's'} marked intentional and kept quiet.
        </Text>
      )}
    </div>
  );
}
